import _ from 'lodash';
import { getLegs as getDeliveryLegs } from '../../libs/deliveryLegs';
import React from 'react';
import PropTypes from 'prop-types';
import moment from 'moment';

// material
import { withStyles } from '@material-ui/core/styles';
import {
	Accordion,
	AccordionDetails,
	AccordionSummary,
	Tooltip,
} from '@material-ui/core';
import Paper from '@material-ui/core/Paper';
import Grid from '@material-ui/core/Grid';
import CircularProgress from '@material-ui/core/CircularProgress';
import Button from '@material-ui/core/Button';
import Checkbox from '@material-ui/core/Checkbox';
import FormControl from '@material-ui/core/FormControl';
import InputLabel from '@material-ui/core/InputLabel';
import ListItemText from '@material-ui/core/ListItemText';
import MenuItem from '@material-ui/core/MenuItem';
import Select from '@material-ui/core/Select';
import Table from '@material-ui/core/Table';
import TableBody from '@material-ui/core/TableBody';
import TableCell from '@material-ui/core/TableCell';
import TableHead from '@material-ui/core/TableHead';
import TableRow from '@material-ui/core/TableRow';
import SearchIcon from '@material-ui/icons/Search';
import DownloadIcon from '@material-ui/icons/CloudDownload';
import ExpandMoreIcon from '@material-ui/icons/ExpandMore';
import KeyboardArrowDownIcon from '@material-ui/icons/KeyboardArrowDown';
import KeyboardArrowRightIcon from '@material-ui/icons/KeyboardArrowRight';
import IconButton from '@material-ui/core/IconButton';
import LinkIcon from '@material-ui/icons/Link';
import CallSplitIcon from '@material-ui/icons/CallSplit';
import WarningRoundedIcon from '@material-ui/icons/WarningRounded';
import OpenInNewIcon from '@material-ui/icons/OpenInNew';
import HelpOutlineIcon from '@material-ui/icons/HelpOutline';
import AddCircleOutlineIcon from '@material-ui/icons/AddCircleOutline';
import CloseIcon from '@material-ui/icons/Close';

// ui
import FMPHeadline from '../../components/ui/FMPHeadline';
import FMPSectionHeader from '../../components/ui/FMPSectionHeader';
import FMPMonthYearPicker from '../../components/pureForm/FMPMonthYearPicker';
import InventoryReportsContainer from '../../containers/inventory/InventoryReports';

import { getSharedStyles } from '../../themes';
import FormComponent from '../../components/FormComponent';
import { search } from '../../modules/inventory/inventoryReports';
import { remove as removeInventoryAdjustment } from '../../modules/inventory/inventoryAdjustment';
import InventoryAdjustmentDialog from './InventoryAdjustmentDialog';
import { post } from '../../modules/utility';
import invoiceIcon from '../../images/invoicePdf.svg';
import ResourcePopup from '../../components/Dialogs/ResourcePopup';

const fifoDarkColors = [
	'rgba(225, 141, 115, 1)',
	'rgba(101, 136, 207, 1)',
	'rgba(178, 207, 57, 1)',
	'rgba(176, 117, 186, 1)',
	'rgba(134, 192, 78, 1)',
	'rgba(214, 113, 177, 1)',
	'rgba(100, 172, 127, 1)',
	'rgba(225, 174, 98, 1)',
	'rgba(111, 179, 195, 1)',
	'rgba(215, 205, 107, 1)',
	'rgba(149, 127, 189, 1)',
];

const currentColumns = [
	{ displayId: 'terminalDisplay', label: 'Terminal', flex: 1.1 },
	{ displayId: 'supplierDisplay', label: 'Supplier', flex: 0.9 },
	{ displayId: 'productDisplay', label: 'Product', flex: 0.5 },
	{ displayId: 'railcarCountDisplay', label: 'Railcars', flex: 0.6 },
	{ displayId: 'deliveryCountDisplay', label: 'Deliveries', flex: 0.6 },
	{ displayId: 'gallonsDisplay', label: 'Gallons', flex: 2 },
];

const styles = (theme) => ({
	...getSharedStyles(theme),
	bigButton: {
		borderRadius: '100px',
		padding: '18px 34px',
		fontSize: 16,
	},
	dateRange: {
		marginRight: 10,
	},
	summaryBar: {
		display: 'flex',
		flexWrap: 'wrap',
		gap: '0 24px',
		padding: '16px 20px',
		background: '#fff',
		borderRadius: 4,
		boxShadow: '0 1px 3px rgba(0,0,0,0.12)',
		marginBottom: 16,
	},
	summaryBarStat: {
		minWidth: 140,
		marginBottom: 8,
	},
	summaryBarLabel: {
		fontSize: '0.7rem',
		color: '#888',
		textTransform: 'uppercase',
		letterSpacing: '0.04em',
	},
	summaryBarValue: {
		fontSize: '1.4rem',
		fontWeight: 700,
		color: '#222',
		lineHeight: 1.2,
	},
	summaryBarDivider: {
		width: 1,
		background: '#e0e0e0',
		margin: '0 8px',
		alignSelf: 'stretch',
	},
	columnHeaderRow: {
		display: 'flex',
		alignItems: 'center',
		padding: '8px 64px 8px 24px',
		background: '#f5f5f5',
		borderBottom: '2px solid #ddd',
		borderRadius: '4px 4px 0 0',
		marginBottom: 0,
	},
	columnHeaderCell: {
		fontSize: '0.7rem',
		fontWeight: 700,
		textTransform: 'uppercase',
		letterSpacing: '0.05em',
		color: '#555',
		paddingRight: 8,
		minWidth: 0,
	},
	columnRow: {
		display: 'flex',
		alignItems: 'flex-start',
		flex: 1,
	},
	columnCell: {
		paddingRight: 8,
		fontSize: '0.875rem',
		textAlign: 'left',
		minWidth: 0,
	},
	columnLabel: {
		fontSize: '0.65rem',
		fontWeight: 700,
		textTransform: 'uppercase',
		letterSpacing: '0.05em',
		color: '#888',
		marginBottom: 2,
	},
	detailsSection: {
		width: '100%',
		flexDirection: 'column',
		overflow: 'hidden',
		boxSizing: 'border-box',
	},
	noRailcars: {
		color: '#888',
		fontStyle: 'italic',
		padding: 8,
	},
	linkedUnionRow: {
		borderLeft: '3px solid #1976d2',
		background: '#f5f9ff',
	},
	linkedBadge: {
		display: 'inline-flex',
		alignItems: 'center',
		color: '#1976d2',
		fontSize: '0.7rem',
		fontWeight: 600,
		marginLeft: 6,
		verticalAlign: 'middle',
	},
	deliverySubRow: {
		background: '#fafafa',
	},
	deliveryTable: {
		fontSize: '0.8rem',
		background: '#f0f4f8',
		margin: '0 0 4px 0',
	},
	invoiceStatus: {
		fontSize: '0.75rem',
		fontWeight: 600,
		padding: '2px 6px',
		borderRadius: 10,
		display: 'inline-block',
	},
	expandToggle: {
		padding: 4,
	},
	inventorySummary: {
		display: 'flex',
		flexDirection: 'row',
		gap: 32,
		margin: 0,
		padding: 0,
	},
	inventorySummaryGroup: {
		display: 'flex',
		flexDirection: 'column',
		gap: 4,
	},
	inventorySummaryGroupHeader: {
		fontSize: '0.65rem',
		fontWeight: 700,
		textTransform: 'uppercase',
		letterSpacing: '0.05em',
		color: '#888',
		marginBottom: 4,
	},
	inventorySummaryItem: {
		display: 'flex',
		fontSize: '0.85rem',
		color: '#333',
	},
	inventorySummaryItemTotal: {
		display: 'flex',
		fontSize: '0.85rem',
		fontWeight: 700,
		color: '#111',
		borderTop: '1px solid #ddd',
		paddingTop: 4,
		marginTop: 2,
	},
	inventorySummaryLabel: {
		color: '#888',
		minWidth: 140,
		textAlign: 'right',
		paddingRight: 8,
	},
	inventorySummaryCell: {
		color: '#222',
		fontFamily: 'monospace',
	},
	inventorySummaryCellNeg: {
		color: '#c62828',
		fontFamily: 'monospace',
	},
	inventorySummaryCellPos: {
		color: '#2e7d32',
		fontFamily: 'monospace',
	},
	railcarTableWrapper: {
		width: '100%',
		overflowX: 'auto',
		minWidth: 0,
	},
	calcNotesItem: {
		fontSize: '0.875rem',
		color: '#333',
		lineHeight: 1.6,
		paddingLeft: 16,
		marginBottom: 6,
		textAlign: 'left',
		'&::before': {
			content: '"•"',
			marginRight: 8,
			color: '#1976d2',
		},
	},
	pdfIcon: {
		display: 'inline-block',
		width: 14,
		height: 17,
		backgroundImage: `url('${invoiceIcon}')`,
		backgroundPosition: 'center',
		backgroundSize: 'cover',
		verticalAlign: 'middle',
		position: 'relative',
		flexShrink: 0,
		cursor: 'pointer',
	},
});

const isLocalhost = Boolean(window.location.hostname === 'localhost');

class InventoryReports extends FormComponent {
	constructor(props) {
		super(props);

		const dateRange = 'thisMonth';
		const { yearMonth } = this.dateRangeToDates(dateRange);
		this.state = {
			dateRange,
			yearMonth,
			yearMonthEnd: yearMonth,
			isSearching: false,
			terminalSupplierGroups: undefined,
			stationDeliveryMap: {},
			stationInvoiceMap: {},
			resourceMap: {},
			stationMap: {},
			terminalMap: {},
			supplierMap: {},
			searchedYearMonth: undefined,
			searchedYearMonthEnd: undefined,
			railcarVendorBillMap: {},
			vendorBillMap: {},
			terminalFilterOptions: [],
			selectedTerminalIds: [],
			calculationNotes: [],
			expandedUnionKeys: new Set(),
			expandedContractKeys: new Set(),
			adjustmentDialogGroupKey: null,
			formFieldError: {},
		};
	}

	componentDidMount() {
		post({
			method: 'terminal.search',
			params: {},
			callback: (err, result) => {
				if (err) {
					return;
				}
				const terminalCollection = (result.collections || {}).terminalCollection || [];
				const terminalFilterOptions = _.sortBy(terminalCollection, (t) => t.name || '');
				const terminalMap = _.keyBy(terminalCollection, 'terminalId');
				const mhxFontana = terminalCollection.find((t) => (t.name || '').toLowerCase().includes('mhx fontana'));
				const selectedTerminalIds = mhxFontana ? [String(mhxFontana.terminalId)] : [];
				this.setState({ terminalFilterOptions, terminalMap, selectedTerminalIds });
			},
		});
	}

	dateRangeToDates = (dateRange) => {
		if (dateRange === 'thisMonth') {
			return { yearMonth: moment().startOf('month').format('YYYY-MM') };
		} else if (dateRange === 'lastMonth') {
			return { yearMonth: moment().subtract(1, 'month').startOf('month').format('YYYY-MM') };
		} else if (dateRange === 'twoMonthsAgo') {
			return { yearMonth: moment().subtract(2, 'months').startOf('month').format('YYYY-MM') };
		} else {
			return {};
		}
	};

	setDateRange = (dateRange) => {
		const { yearMonth } = this.dateRangeToDates(dateRange);
		if (!yearMonth) {
			this.setState({ dateRange });
		} else {
			this.setState({ dateRange, yearMonth, yearMonthEnd: yearMonth });
		}
	};

	toggleUnionExpanded = (unionKey) => {
		this.setState((prev) => {
			const next = new Set(prev.expandedUnionKeys);
			if (next.has(unionKey)) {
				next.delete(unionKey);
			} else {
				next.add(unionKey);
			}
			return { expandedUnionKeys: next };
		});
	};

	toggleContractExpanded = (fuelContractId) => {
		this.setState((prev) => {
			const next = new Set(prev.expandedContractKeys);
			if (next.has(fuelContractId)) {
				next.delete(fuelContractId);
			} else {
				next.add(fuelContractId);
			}
			return { expandedContractKeys: next };
		});
	};

	submitSearch = () => {
		const { showErrors } = this.props;
		const { yearMonth, yearMonthEnd, selectedTerminalIds } = this.state;

		this.setState({ isSearching: true });

		const afterDate = yearMonth
			? moment(yearMonth, 'YYYY-MM').startOf('month').format('YYYY-MM-DD')
			: undefined;

		const beforeDate = yearMonthEnd
			? moment(yearMonthEnd, 'YYYY-MM').endOf('month').format('YYYY-MM-DD')
			: undefined;

		search({
			afterDate,
			beforeDate,
			terminalIds: selectedTerminalIds.length > 0 ? selectedTerminalIds : undefined,
			callback: (err, result) => {
				this.setState({ isSearching: false });
				if (err) {
					return showErrors(_.isArray(err) ? err : [err]);
				}

				const terminalSupplierGroups = result.terminalSupplierGroups || [];
				const calculationNotes = result.calculationNotes || [];

				const {
					stationDeliveryCollection,
					stationInvoiceCollection,
					stationCollection,
					terminalCollection,
					supplierCollection,
					railcarVendorBillCollection,
					resourceCollection,
				} = result.collections;

				const searchTerminalMap = _.keyBy(terminalCollection || [], 'terminalId');

				this.setState((prev) => {
					//- The search response only carries the terminals that showed up in the results
					//- (often none at all), so merge onto the full list loaded on mount instead of
					//- replacing it — otherwise the filter dropdown empties out after a search.
					const terminalMap = { ...prev.terminalMap, ...searchTerminalMap };
					const terminalFilterOptions = _.sortBy(_.values(terminalMap), (t) => t.name || '');

					return {
						terminalSupplierGroups,
						//- Pin the range these results came from. The month pickers can be
						//- changed without re-searching, and the allocation links have to
						//- follow what is on screen rather than the picker.
						searchedYearMonth: yearMonth,
						searchedYearMonthEnd: yearMonthEnd || yearMonth,
						stationDeliveryMap: _.keyBy(stationDeliveryCollection || [], 'stationDeliveryId'),
						stationInvoiceMap: _.keyBy(stationInvoiceCollection || [], 'stationInvoiceId'),
						resourceMap: _.keyBy(resourceCollection || [], 'resourceId'),
						stationMap: _.keyBy(stationCollection || [], 'stationId'),
						terminalMap,
						supplierMap: _.keyBy(supplierCollection || [], 'supplierId'),
						railcarVendorBillMap: _.keyBy(railcarVendorBillCollection || [], 'linkedRailcarId'),
						vendorBillMap: _.keyBy(railcarVendorBillCollection || [], 'vendorBillOrderId'),
						terminalFilterOptions,
						calculationNotes,
						expandedUnionKeys: new Set(),
						expandedContractKeys: new Set(),
					};
				});
			},
		});
	};

	//- Refresh the range that is on screen. The pickers can drift from the searched range
	//- without re-searching, so snap them back to what the results actually show first.
	refreshSearchedRange = () => {
		const { searchedYearMonth, searchedYearMonthEnd } = this.state;
		this.setState(
			{ yearMonth: searchedYearMonth, yearMonthEnd: searchedYearMonthEnd },
			() => this.submitSearch()
		);
	};

	openAdjustmentDialog = (group) => {
		this.setState({ adjustmentDialogGroupKey: group.inventoryPeriodKey });
	};

	closeAdjustmentDialog = () => {
		this.setState({ adjustmentDialogGroupKey: null });
	};

	handleAdjustmentSaved = () => {
		this.setState({ adjustmentDialogGroupKey: null });
		this.refreshSearchedRange();
	};

	handleRemoveAdjustment = (adjustment) => {
		const { showErrors } = this.props;
		const confirmed = window.confirm(
			`Remove this ${new Intl.NumberFormat('en-US').format(adjustment.adjustmentGallons)} ` +
				`gallon adjustment?\n\nReason was: ${adjustment.reason}`
		);
		if (!confirmed) {
			return;
		}
		removeInventoryAdjustment({
			inventoryAdjustmentId: adjustment.inventoryAdjustmentId,
			callback: (err) => {
				if (err) {
					return showErrors(_.isArray(err) ? err : [err]);
				}
				this.refreshSearchedRange();
			},
		});
	};

	renderDateButton = (label, range) => {
		const { classes } = this.props;
		const { dateRange } = this.state;
		const variant = dateRange === range ? 'contained' : 'outlined';

		return (
			<Button
				className={classes.dateRange}
				variant={variant}
				color="primary"
				onClick={() => this.setDateRange(range)}
			>
				{label}
			</Button>
		);
	};

	renderDateRangeOptions = () => {
		const { dateRange } = this.state;
		const isCustomRange = dateRange === 'custom';

		return (
			<>
				<Grid item md={12}>
					<FMPSectionHeader label={'Date Range'} isTop={true} />
				</Grid>

				<Grid item md={12} style={{ display: 'flex' }}>
					{this.renderDateButton('Current Month', 'thisMonth')}
					{this.renderDateButton('Last Month', 'lastMonth')}
					{this.renderDateButton('Two Months Prior', 'twoMonthsAgo')}
					{this.renderDateButton('Custom', 'custom')}
				</Grid>

				<FMPMonthYearPicker
					{...this.setForm('yearMonth', isCustomRange ? 'Start Month / Year' : 'Month / Year')}
					disabled={!isCustomRange}
					showFuture={true}
					md={6}
				/>

				{isCustomRange && (
					<FMPMonthYearPicker
						{...this.setForm('yearMonthEnd', 'End Month / Year')}
						disabled={false}
						showFuture={true}
						md={6}
					/>
				)}

				<Grid item md={12}>
					<FMPSectionHeader label={'Terminals'} />
				</Grid>
				<Grid item md={12}>
					{this.renderTerminalFilter()}
				</Grid>
			</>
		);
	};

	renderSearchOptions = () => {
		const { classes } = this.props;
		const { isSearching, yearMonth, yearMonthEnd, selectedTerminalIds } = this.state;
		const { accessToken } = this.props;

		return (
			<>
				<FMPHeadline label="Inventory Reports" />
				<Paper className={classes.paper}>
					<Grid container spacing={2}>
						{this.renderDateRangeOptions()}

						<Grid item md={12} style={{ paddingTop: 16, display: 'flex', alignItems: 'center' }}>
							<Button
								className={classes.bigButton}
								variant="contained"
								color="primary"
								onClick={this.submitSearch}
								disabled={isSearching}
							>
								<SearchIcon className={classes.icon} />
								Search
							</Button>

							{(() => {
								const afterDate = yearMonth ? `${yearMonth}-01` : undefined;
								const beforeDate = yearMonthEnd
									? moment(yearMonthEnd, 'YYYY-MM').endOf('month').format('YYYY-MM-DD')
									: undefined;
								const params = { accessToken, afterDate, beforeDate, terminalIds: selectedTerminalIds, downloadCsv: true };
								const request = { method: 'inventoryReports.search', params };
								let host = '';
								if (isLocalhost) {
									host = 'http://localhost:3700';
								}
								const downloadUrl = `${host}/api?request=${JSON.stringify(request)}`;
								return (
									<a
										href={downloadUrl}
										style={{ textDecoration: 'none', paddingLeft: 10 }}
										target="_blank"
										rel="noreferrer noopener"
									>
										<Button
											className={classes.bigButton}
											variant="contained"
											color="primary"
											disabled={isSearching}
										>
											<DownloadIcon className={classes.icon} />
											&nbsp;Download CSV
										</Button>
									</a>
								);
							})()}

							{isSearching && (
								<div className={classes.runningSearch} style={{ marginLeft: 16 }}>
									<CircularProgress />
									<span style={{ paddingLeft: 5 }}>Searching...</span>
								</div>
							)}
						</Grid>
					</Grid>
				</Paper>
			</>
		);
	};

	renderSummaryBar = (visibleGroups) => {
		const { classes } = this.props;

		//- One terminal can now produce several groups (one per supplier + product), so count
		//- distinct terminals rather than groups.
		const totalTerminals = _.uniq(_.map(visibleGroups, 'terminalId')).length;
		const totalRailcars = _.sumBy(visibleGroups, (g) =>
			_.sumBy(g.railcarUnions, (u) => u.railcars.length)
		);
		const totalDeliveries = _.sumBy(visibleGroups, (g) =>
			_.sumBy(g.railcarUnions, (u) => (u.linkedDeliveryLegs || []).filter((leg) => !leg.isNotInRange).length)
		);
		const gallons = _.sumBy(visibleGroups, 'soldGallons') || 0;

		const stat = (label, value) => (
			<div className={classes.summaryBarStat}>
				<div className={classes.summaryBarLabel}>{label}</div>
				<div className={classes.summaryBarValue}>{value}</div>
			</div>
		);

		return (
			<div className={classes.summaryBar}>
				{stat('Terminals', totalTerminals.toLocaleString())}
				{stat('Inventory Pools', visibleGroups.length.toLocaleString())}
				<div className={classes.summaryBarDivider} />
				{stat('Railcars', totalRailcars.toLocaleString())}
				{stat('Deliveries', totalDeliveries.toLocaleString())}
				{stat('Net Gallons', gallons.toLocaleString())}
			</div>
		);
	};


	handlePdfClick = (resourceId) => {
		this.setState({
			resourceIdsPoppedUp: [resourceId],
			isResourcePopupOpen: true,
		});
	};

	handleCloseResourcePopup = () => {
		this.setState({
			isResourcePopupOpen: false,
			resourceIdsPoppedUp: [],
		});
	};

	formatBolPdfCell(bol, bolResourceId) {
		const { classes } = this.props;
		return (
			<div style={{ display: 'flex' }}>
				<div style={{ paddingRight: '5px' }}>{bol}</div>

				{bolResourceId ? (
					<div
						className={classes.pdfIcon}
						onClick={() => {
							this.handlePdfClick(bolResourceId);
						}}
					/>
				) : (
					<div className={classes.pdfIcon} style={{ opacity: '.3' }} />
				)}
			</div>
		);
	}

	renderDeliveryRows = (union) => {
		const { classes } = this.props;
		const { stationDeliveryMap, stationInvoiceMap, stationMap } = this.state;

		const isLinkedUnion = union.railcars.length > 1;

		const allLegs = union.linkedDeliveryLegs || [];
		const sortedLegs = _.sortBy(
			allLegs.filter((leg) => !leg.isNotInRange),
			(leg) => {
				const delivery = stationDeliveryMap[leg.stationDeliveryId];
				return delivery ? delivery.deliveryWindowStartDateTime : '';
			}
		);

		const outOfRangeCount = allLegs.filter((leg) => leg.isNotInRange).length;

		const fmtDate = (d) => (d ? moment(d).format('M/D/YYYY') : '-');
		const formatNum = (val) => (val == null ? '-' : new Intl.NumberFormat('en-US').format(val));
		const formatCurrency = (val) =>
			val != null
				? `$${Number(val).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`
				: '-';

		const statusColor = (status) => {
			if (!status) return '#aaa';
			if (status === 'issued' || status === 'sent') return '#4CAF50';
			if (status === 'invalid') return '#f44336';
			return '#FF9800';
		};

		const subTableSectionLabel = (text) => (
			<div style={{ fontSize: '0.75rem', fontWeight: 600, color: '#555', marginTop: 8, marginBottom: 2 }}>
				{text}
			</div>
		);





		return (
			<TableRow className={classes.deliverySubRow}>
				<TableCell colSpan={12} style={{ padding: '4px 8px 8px 48px' }}>
					{subTableSectionLabel('Deliveries')}
					<Table size="small" className={classes.deliveryTable}>
						<TableHead>
							<TableRow>
								{isLinkedUnion && <TableCell>Railcar</TableCell>}
								<TableCell>Delivery ID</TableCell>
								<TableCell>Station #</TableCell>
								<TableCell>Delivery Date</TableCell>
								<TableCell>Tank #</TableCell>
								<TableCell>Invoice #</TableCell>
								<TableCell>Invoice Date</TableCell>
								<TableCell>Invoice Status</TableCell>
								<TableCell>Invoice Total</TableCell>
								<TableCell>Invoiced Gal (sold)</TableCell>
								<TableCell>BOL Gal</TableCell>
								<TableCell>BOL</TableCell>
							</TableRow>
						</TableHead>
						<TableBody>
							{_.isEmpty(sortedLegs) && (
								<TableRow>
									<TableCell
										colSpan={isLinkedUnion ? 10 : 9}
										style={{ fontStyle: 'italic', color: '#888', fontSize: '0.75rem', textAlign: 'center', padding: '6px 8px' }}
									>
										{outOfRangeCount > 0
											? `${outOfRangeCount} ${outOfRangeCount === 1 ? 'delivery' : 'deliveries'} linked to this railcar but outside the date range`
											: 'No invoiced deliveries in this period'}
									</TableCell>
								</TableRow>
							)}
							{(() => {
							const deliveryColorMap = {};
							let colorIdx = 0;
							sortedLegs.forEach((leg) => {
								if (!(leg.stationDeliveryId in deliveryColorMap)) {
									deliveryColorMap[leg.stationDeliveryId] = colorIdx++;
								}
							});
							return sortedLegs.map((leg) => {
								const delivery = stationDeliveryMap[leg.stationDeliveryId];
								const invoice = stationInvoiceMap[leg.matchedStationInvoiceId];
								const deliveryDate = delivery ? fmtDate(delivery.deliveryWindowStartDateTime) : '-';
								const mapRailcar = isLinkedUnion
									? union.railcars.find((r) => r.railcarId === leg.railcarId)
									: null;
								const station = leg.stationId ? stationMap[leg.stationId] : null;
								const stationNumber = station ? station.number : '-';
								const legColor =
									fifoDarkColors[deliveryColorMap[leg.stationDeliveryId] % fifoDarkColors.length];

								let splitSuffix = null;
								if (delivery && delivery.isSplit) {
									const matchedLeg = getDeliveryLegs(delivery).find(
										(deliveryLeg) => deliveryLeg.stationId === leg.stationId
									);
									if (matchedLeg) {
										splitSuffix = (
											<span style={{ fontSize: '70%', verticalAlign: 'super', marginLeft: 2 }}>
												({matchedLeg.legNumber})
											</span>
										);
									}
								}

								return (
									<TableRow key={`${leg.stationDeliveryId}_${leg.stationId}`}>
										{isLinkedUnion && (
											<TableCell>
												<span style={{ fontSize: '0.75rem', color: '#555' }}>
													{mapRailcar ? mapRailcar.railcarNumber : leg.railcarId}
												</span>
											</TableCell>
										)}
										<TableCell>
											<span style={{ display: 'flex', alignItems: 'center', gap: 2 }}>
												<span
													style={{
														display: 'inline-block',
														backgroundColor: legColor,
														width: 8,
														height: 8,
														marginRight: 3,
														flexShrink: 0,
													}}
												/>
												{leg.stationDeliveryId}
												{splitSuffix}
												<a
													href={`/logistics/stationDelivery/update/${leg.stationDeliveryId}`}
													target="_blank"
													rel="noopener noreferrer"
													style={{ display: 'flex', alignItems: 'center', color: '#1976d2', marginLeft: 4 }}
												>
													<LinkIcon style={{ fontSize: '0.85rem' }} />
												</a>
											</span>
										</TableCell>
										<TableCell>
											<span style={{ fontSize: '0.75rem', fontWeight: 600 }}>{stationNumber}</span>
										</TableCell>
										<TableCell>{deliveryDate}</TableCell>
										<TableCell>{leg.tankNumber || '-'}</TableCell>

										<TableCell>
											{invoice ? (
												<a
													href={`/accounting/stationInvoice/update/${invoice.stationInvoiceId}`}
													target="_blank"
													rel="noopener noreferrer"
													style={{ color: '#1976d2', textDecoration: 'none' }}
												>
													{invoice.stationInvoiceId}
												</a>
											) : (
												<span style={{ color: '#aaa', fontStyle: 'italic' }}>No invoice</span>
											)}
										</TableCell>
										<TableCell>{invoice ? fmtDate(invoice.issueDate) : '-'}</TableCell>
										<TableCell>
											{invoice ? (
												<span
													className={classes.invoiceStatus}
													style={{
														background: statusColor(invoice.status) + '22',
														color: statusColor(invoice.status),
													}}
												>
													{invoice.status || '-'}
												</span>
											) : '-'}
										</TableCell>
										<TableCell>
											{invoice && invoice.total != null ? (
												leg.partialInvoiceTotalCost != null ? (
													<div>
														<span style={{ display: 'inline-flex', alignItems: 'center', gap: 2 }}>
															{formatCurrency(leg.partialInvoiceTotalCost)}
															{leg.isSplitReason && (
																<Tooltip title={<span style={{ fontSize: '0.85rem' }}>{leg.isSplitReason}</span>}>
																	<CallSplitIcon style={{ color: '#1976d2', fontSize: 14, cursor: 'default' }} />
																</Tooltip>
															)}
														</span>
														<div style={{ fontSize: '0.7rem', color: '#aaa' }}>{formatCurrency(invoice.total)}</div>
													</div>
												) : (
													formatCurrency(invoice.total)
												)
											) : '-'}
										</TableCell>
										<TableCell>
											<span style={{ display: 'inline-flex', alignItems: 'center', gap: 2 }}>
												{leg.invoicedNetGallonError && (
													<Tooltip title={<span style={{ fontSize: '0.85rem' }}>{leg.invoicedNetGallonError}</span>}>
														<WarningRoundedIcon style={{ color: '#f57c00', fontSize: 14, cursor: 'default' }} />
													</Tooltip>
												)}
												{formatNum(leg.invoicedNetGallons)}
											</span>
										</TableCell>
										<TableCell>{formatNum(leg.netGallons)}</TableCell>
										<TableCell>{this.formatBolPdfCell(leg.bol, leg.bolResourceId)}</TableCell>

									</TableRow>
								);
							});
						})()}
						{outOfRangeCount > 0 && !_.isEmpty(sortedLegs) && (
								<TableRow>
									<TableCell
										colSpan={isLinkedUnion ? 10 : 9}
										style={{ fontStyle: 'italic', color: '#888', fontSize: '0.75rem', textAlign: 'center', padding: '6px 8px' }}
									>
										{outOfRangeCount} {outOfRangeCount === 1 ? 'delivery' : 'deliveries'} linked to this railcar but outside the date range
									</TableCell>
								</TableRow>
							)}
						</TableBody>
					</Table>
				</TableCell>
			</TableRow>
		);
	};

	/**
	 * Where a beginning / ending inventory number lives on the Inventory Allocation page.
	 *
	 * Beginning inventory for month M is the previousMonthRollOver row dated M-01, which is
	 * the "Previous Rollover" line on M's allocation. Ending inventory for month M is written
	 * as the rollover dated (M+1)-01, so it shows up on the FOLLOWING month's allocation.
	 *
	 * Deep-links to the single allocation when the group maps to exactly one bucket. It does
	 * not when any of the group's contracts carries a loading number, because the allocation
	 * key appends that (getInventoryAllocationId) and one report group then spans several
	 * buckets -- Shell Carson is the real case. There we link to the month's allocation list,
	 * which always resolves.
	 */
	buildAllocationLink = (group, yearMonthValue) => {
		if (!yearMonthValue) {
			return null;
		}
		const hasLoadingNumbers = _.some(
			group.contracts || [],
			(c) => !!_.trim(c.contractLoadingNumber || '')
		);
		if (hasLoadingNumbers || !group.inventoryPeriodKey) {
			return {
				url: `/inventoryManagement/inventoryAllocations/${yearMonthValue}`,
				isList: true,
			};
		}
		return {
			url: `/inventoryManagement/inventoryAllocation/details/${group.inventoryPeriodKey}/${yearMonthValue}`,
			isList: false,
		};
	};

	renderInventorySummary = (group) => {
		const { classes } = this.props;
		const { searchedYearMonth, searchedYearMonthEnd } = this.state;

		const {
			beginningInventoryGallons,
			purchasedGallons,
			soldGallons,
			gainLossForTimeFrame,
			endingInventoryGallons,
			purchasedGallonsWarning = null,
		} = group;

		const fmtGal = (n) => {
			if (n == null) return '-';
			const abs = Math.round(Math.abs(n));
			const fmt = new Intl.NumberFormat('en-US').format(abs);
			return n < 0 ? `(${fmt})` : fmt;
		};
		const galClass = (n) =>
			n < 0 ? classes.inventorySummaryCellNeg : n > 0 ? classes.inventorySummaryCellPos : '';

		//- Beginning inventory is read off the report's FIRST month; ending inventory is
		//- posted to the month after the report's LAST month.
		const beginningLink = this.buildAllocationLink(group, searchedYearMonth);
		const endingMonth = searchedYearMonthEnd
			? moment(searchedYearMonthEnd, 'YYYY-MM').add(1, 'month').format('YYYY-MM')
			: undefined;
		const endingLink = this.buildAllocationLink(group, endingMonth);

		const monthName = (ym) => (ym ? moment(ym, 'YYYY-MM').format('MMMM YYYY') : '');

		const rows = [
			{
				label: 'Beg. Inventory',
				gallons: beginningInventoryGallons,
				link: beginningLink,
				linkTooltip:
					`This is the Previous Rollover on the ${monthName(searchedYearMonth)} inventory ` +
					`allocation — last month's ending inventory carried forward. ` +
					`Opens ${beginningLink && beginningLink.isList
						? `the ${monthName(searchedYearMonth)} allocation list`
						: `that allocation`} in a new tab.`,
			},
			{ label: 'Purchased', gallons: purchasedGallons, warning: purchasedGallonsWarning },
			{ label: 'Sold', gallons: -soldGallons },
			{ label: 'Gain / Loss', gallons: gainLossForTimeFrame },
			//- Manual adjustments, one line each, already included in the backend's
			//- endingInventoryGallons. The (?) tooltip explains who/when/why.
			...(group.adjustments || []).map((adjustment, i) => ({
				label: `Adjust #${i + 1}`,
				gallons: adjustment.adjustmentGallons,
				adjustment,
			})),
			{
				label: 'End. Inventory',
				gallons: endingInventoryGallons,
				isTotal: true,
				link: endingLink,
				linkTooltip:
					`This gets posted as the Previous Rollover on the ${monthName(endingMonth)} ` +
					`inventory allocation, where it becomes next month's starting inventory. ` +
					`Opens ${endingLink && endingLink.isList
						? `the ${monthName(endingMonth)} allocation list`
						: `that allocation`} in a new tab.`,
			},
		];

		const gallonsDisplay = (
			<div className={classes.inventorySummaryGroup}>
				{rows.map((row) => (
					<div key={row.label} className={row.isTotal ? classes.inventorySummaryItemTotal : classes.inventorySummaryItem}>
						<span className={classes.inventorySummaryLabel} style={{ display: 'inline-flex', alignItems: 'center' }}>
							{row.label}:
							{row.warning && (
								<Tooltip title={<span style={{ fontSize: '0.85rem' }}>{row.warning}</span>}>
									<WarningRoundedIcon style={{ color: '#f57c00', marginLeft: 4, fontSize: 14, cursor: 'default' }} />
								</Tooltip>
							)}
							{row.link && (
								<Tooltip title={<span style={{ fontSize: '0.85rem' }}>{row.linkTooltip}</span>}>
									<a
										href={row.link.url}
										target="_blank"
										rel="noopener noreferrer"
										onClick={(e) => e.stopPropagation()}
										style={{ display: 'inline-flex', alignItems: 'center', marginLeft: 4, color: '#1976d2' }}
									>
										<OpenInNewIcon style={{ fontSize: 13 }} />
									</a>
								</Tooltip>
							)}
							{row.adjustment && (
								<Tooltip
									title={
										<span style={{ fontSize: '0.85rem', whiteSpace: 'pre-line' }}>
											{`${fmtGal(row.adjustment.adjustmentGallons)} gallons\n` +
												`Entered by ${row.adjustment.createdByName || 'unknown'} on ` +
												`${moment(row.adjustment.dateCreated).format('MMM D, YYYY h:mm a')}\n` +
												`Reason: ${row.adjustment.reason}`}
										</span>
									}
								>
									<HelpOutlineIcon
										style={{ color: '#1976d2', marginLeft: 4, fontSize: 13, cursor: 'default' }}
										onClick={(e) => e.stopPropagation()}
									/>
								</Tooltip>
							)}
							{row.adjustment && (
								<Tooltip title={<span style={{ fontSize: '0.85rem' }}>Remove this adjustment</span>}>
									<CloseIcon
										style={{ color: '#c62828', marginLeft: 2, fontSize: 13, cursor: 'pointer' }}
										onClick={(e) => {
											e.stopPropagation();
											this.handleRemoveAdjustment(row.adjustment);
										}}
									/>
								</Tooltip>
							)}
						</span>
						<span className={galClass(row.gallons) || classes.inventorySummaryCell}>{fmtGal(row.gallons)}</span>
					</div>
				))}
				<div className={classes.inventorySummaryItem}>
					<span
						onClick={(e) => {
							e.stopPropagation();
							this.openAdjustmentDialog(group);
						}}
						style={{
							display: 'inline-flex',
							alignItems: 'center',
							color: '#1976d2',
							cursor: 'pointer',
							fontSize: '0.75rem',
						}}
					>
						<AddCircleOutlineIcon style={{ fontSize: 13, marginRight: 3 }} />
						Adjust
					</span>
				</div>
			</div>
		);

		return { gallonsDisplay };
	};

	renderRailcarTable = (railcarUnionsWithDeliveries) => {
		const { classes } = this.props;
		const { expandedUnionKeys } = this.state;

		if (_.isEmpty(railcarUnionsWithDeliveries)) {
			return <div className={classes.noRailcars}>No railcars matched this contract</div>;
		}

		const fmtDate = (d) => (d ? moment(d).format('MMM-D') : '-');
		const formatNum = (val) => (val == null ? '-' : new Intl.NumberFormat('en-US').format(val));
		//const fmtCost = (n) => {
		//	if (n == null) return <span style={{ color: '#aaa', fontStyle: 'italic' }}>No bill</span>;
		//	return `$${n.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
		//};

		const getContractAgeStyle = (railcar) => {
			if (railcar.contractInCurrentTimeFrame) {
				return { backgroundColor: '#e8f5e9' };
			}
			if (railcar.monthsContractOutsideCurrentTimeFrame === 1) {
				return { backgroundColor: '#fffde7' };
			}
			if (railcar.monthsContractOutsideCurrentTimeFrame >= 2) {
				return { backgroundColor: '#fff3e0' };
			}
			return {};
		};

		/*
		const getVendorBillCell = (railcar) => {
			const bill = railcarVendorBillMap[railcar.railcarId];
			if (!bill) {
				if (railcar.isForThisSupplier === false) {
					return '-';
				}
				return <span style={{ color: '#e65100', fontWeight: 500 }}>Missing</span>;
			}
			return (
				<a
					href={`/billing/vendorBill/update/${bill.vendorBillId}`}
					target="_blank"
					rel="noopener noreferrer"
					style={{ color: '#1976d2', textDecoration: 'none', fontSize: '0.8rem' }}
				>
					{bill.billNumber || bill.vendorBillId}
				</a>
			);
		};
		*/

		return (
			<div className={classes.railcarTableWrapper}>
				<Table size="small">
					<TableHead>
						<TableRow>
							<TableCell style={{ width: 40 }}>Del</TableCell>
							<TableCell>Railcar #</TableCell>
							<TableCell>Contract #</TableCell>
							<TableCell>Fuel Vendor / Product</TableCell>
							<TableCell>PO #</TableCell>
							<TableCell>CP / AP Date</TableCell>
							<TableCell>Empty <br /> Rel Date</TableCell>
							<TableCell>Sold<br />Gallons</TableCell>
							<TableCell>BOL<br />Gallons</TableCell>
							<TableCell>Railcar<br />Gallons</TableCell>
							<TableCell>Gain/Loss<br />Gallons</TableCell>
						</TableRow>
					</TableHead>
					<TableBody>
						{railcarUnionsWithDeliveries.map((union) => {
							const primaryRailcar = union.railcars[0];
							const unionKey = primaryRailcar.railcarId;
							const isLinked = union.railcars.length > 1;
							const isExpanded = expandedUnionKeys.has(unionKey);
							const allLegs = union.linkedDeliveryLegs || [];
							const deliveryCount = allLegs.filter((leg) => !leg.isNotInRange).length;
							const hasAnyDeliveries = allLegs.length > 0;

							if (isLinked) {
								return (
									<React.Fragment key={unionKey}>
										{union.railcars.map((railcar, idx) => {
											const poDisplay = railcar.requestedMonth
												? `${moment(railcar.requestedMonth).format('MMM')} (#${railcar.requestedNumber || '-'})`
												: '-';
											const railcarLegErrors = allLegs
												.filter((leg) => leg.railcarId === railcar.railcarId && leg.invoicedNetGallonError)
												.map((leg) => leg.invoicedNetGallonError);
											return (
												<TableRow key={railcar.railcarId} className={classes.linkedUnionRow} style={{ ...getContractAgeStyle(railcar), ...(railcar.isForThisSupplier === false ? { backgroundColor: '#f5f5f5', color: '#aaa' } : {}) }}>
													{idx === 0 && (
														<TableCell rowSpan={union.railcars.length} style={{ verticalAlign: 'middle', textAlign: 'center' }}>
															<IconButton
																className={classes.expandToggle}
																size="small"
																onClick={() => this.toggleUnionExpanded(unionKey)}
																disabled={!hasAnyDeliveries}
															>
																{isExpanded ? <KeyboardArrowDownIcon /> : <KeyboardArrowRightIcon />}
															</IconButton>
															<div style={{ fontSize: '0.8em', color: deliveryCount > 0 ? '#1976d2' : '#aaa', fontWeight: deliveryCount > 0 ? 600 : 400, lineHeight: 1 }}>
																{deliveryCount}
															</div>
														</TableCell>
													)}
													<TableCell>
														<span style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
															{idx > 0 && <LinkIcon style={{ fontSize: 12, color: '#1976d2' }} />}
															{railcar.isSplitUnion && <CallSplitIcon style={{ fontSize: 14, color: '#1976d2' }} titleAccess="split between suppliers" />}
															{railcar.railcarNumber || '-'}
														</span>
													</TableCell>
													<TableCell>{railcar.fuelContractId || '-'}</TableCell>
													<TableCell>
														<div>{railcar.fuelSupplier || '-'}</div>
														<div style={{ fontSize: '0.75rem', color: '#666' }}>{railcar.product || '-'}</div>
													</TableCell>
													<TableCell>{poDisplay}</TableCell>
													<TableCell>
														<div><span style={{ color: '#888', fontSize: '0.7rem', fontWeight: 600 }}>CP:</span> {fmtDate(railcar.cpDate)}</div>
														<div><span style={{ color: '#888', fontSize: '0.7rem', fontWeight: 600 }}>AP:</span> {fmtDate(railcar.apDate)}</div>
													</TableCell>
													<TableCell>
														<div><span style={{ color: '#888', fontSize: '0.7rem', fontWeight: 600 }}>Emp:</span> {fmtDate(railcar.emptyDate)}</div>
														<div><span style={{ color: '#888', fontSize: '0.7rem', fontWeight: 600 }}>Rel:</span> {fmtDate(railcar.relinquishedDate)}</div>
													</TableCell>
													<TableCell>
														<span style={{ display: 'inline-flex', alignItems: 'center', gap: 2 }}>
															{railcarLegErrors.length > 0 && (
																<Tooltip title={<span style={{ fontSize: '0.85rem' }}><ul style={{ margin: 0, paddingLeft: 16 }}>{railcarLegErrors.map((err, i) => <li key={i}>{err}</li>)}</ul></span>}>
																	<WarningRoundedIcon style={{ color: '#f57c00', fontSize: 14, cursor: 'default' }} />
																</Tooltip>
															)}
															{railcar.soldGallons != null ? formatNum(railcar.soldGallons) : '-'}
														</span>
													</TableCell>
													<TableCell>{formatNum(railcar.netGallonsOutForTimeFrame)}</TableCell>
													<TableCell>{formatNum(railcar.netGallons)}</TableCell>
													<TableCell style={{ color: railcar.gainLossForTimeFrame < 0 ? '#c62828' : undefined }}>
														{railcar.isForThisSupplier === false ? '-' : formatNum(railcar.gainLossForTimeFrame)}
													</TableCell>
												</TableRow>
											);
										})}
										{isExpanded && this.renderDeliveryRows(union)}
									</React.Fragment>
								);
							}

							const poDisplay = primaryRailcar.requestedMonth
								? `${moment(primaryRailcar.requestedMonth).format('MMM')} (#${primaryRailcar.requestedNumber || '-'})`
								: '-';
							const primaryLegErrors = allLegs
								.filter((leg) => leg.invoicedNetGallonError)
								.map((leg) => leg.invoicedNetGallonError);

							return (
								<React.Fragment key={unionKey}>
									<TableRow style={{ ...getContractAgeStyle(primaryRailcar), ...(primaryRailcar.isForThisSupplier === false ? { backgroundColor: '#f5f5f5', color: '#aaa' } : {}) }}>
										<TableCell style={{ textAlign: 'center' }}>
											<IconButton
												className={classes.expandToggle}
												size="small"
												onClick={() => this.toggleUnionExpanded(unionKey)}
												disabled={!hasAnyDeliveries}
											>
												{isExpanded ? <KeyboardArrowDownIcon /> : <KeyboardArrowRightIcon />}
											</IconButton>
											<div style={{ fontSize: '0.8em', color: deliveryCount > 0 ? '#1976d2' : '#aaa', fontWeight: deliveryCount > 0 ? 600 : 400, lineHeight: 1 }}>
												{deliveryCount}
											</div>
										</TableCell>
										<TableCell>
											<span style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
												{primaryRailcar.isSplitUnion && <CallSplitIcon style={{ fontSize: 14, color: '#1976d2' }} titleAccess="split between suppliers" />}
												{primaryRailcar.railcarNumber || '-'}
											</span>
										</TableCell>
										<TableCell>{primaryRailcar.fuelContractId || '-'}</TableCell>
										<TableCell>
											<div>{primaryRailcar.fuelSupplier || '-'}</div>
											<div style={{ fontSize: '0.75rem', color: '#666' }}>{primaryRailcar.product || '-'}</div>
										</TableCell>
										<TableCell>{poDisplay}</TableCell>
										<TableCell>
											<div><span style={{ color: '#888', fontSize: '0.7rem', fontWeight: 600 }}>CP:</span> {fmtDate(primaryRailcar.cpDate)}</div>
											<div><span style={{ color: '#888', fontSize: '0.7rem', fontWeight: 600 }}>AP:</span> {fmtDate(primaryRailcar.apDate)}</div>
										</TableCell>
										<TableCell>
											<div><span style={{ color: '#888', fontSize: '0.7rem', fontWeight: 600 }}>Emp:</span> {fmtDate(primaryRailcar.emptyDate)}</div>
											<div><span style={{ color: '#888', fontSize: '0.7rem', fontWeight: 600 }}>Rel:</span> {fmtDate(primaryRailcar.relinquishedDate)}</div>
										</TableCell>
										<TableCell>
											<span style={{ display: 'inline-flex', alignItems: 'center', gap: 2 }}>
												{primaryLegErrors.length > 0 && (
													<Tooltip title={<span style={{ fontSize: '0.85rem' }}><ul style={{ margin: 0, paddingLeft: 16 }}>{primaryLegErrors.map((err, i) => <li key={i}>{err}</li>)}</ul></span>}>
														<WarningRoundedIcon style={{ color: '#f57c00', fontSize: 14, cursor: 'default' }} />
													</Tooltip>
												)}
												{primaryRailcar.soldGallons != null ? formatNum(primaryRailcar.soldGallons) : '-'}
											</span>
										</TableCell>
										<TableCell>{formatNum(primaryRailcar.netGallonsOutForTimeFrame)}</TableCell>
										<TableCell>{formatNum(primaryRailcar.netGallons)}</TableCell>
										<TableCell style={{ color: primaryRailcar.gainLossForTimeFrame < 0 ? '#c62828' : undefined }}>
											{primaryRailcar.isForThisSupplier === false ? '-' : formatNum(primaryRailcar.gainLossForTimeFrame)}
										</TableCell>
									</TableRow>
									{isExpanded && this.renderDeliveryRows(union)}
								</React.Fragment>
							);
						})}
					</TableBody>
				</Table>
			</div>
		);
	};

	renderContractsTable = (contracts) => {
		const { classes } = this.props;
		const { expandedContractKeys, supplierMap } = this.state;

		const fmtDate = (d) => (d ? moment(d).format('M/D/YYYY') : '-');
		const formatNum = (val) => (val == null ? '-' : new Intl.NumberFormat('en-US').format(val));

		return (
			<div style={{ marginTop: 16 }}>
				<div style={{ fontSize: '0.8rem', fontWeight: 600, color: '#555', marginBottom: 4 }}>
					Fuel Contracts
				</div>
				{_.isEmpty(contracts) ? (
					<div className={classes.noRailcars}>
						No fuel contracts matched this terminal / supplier / product for this period
					</div>
				) : (
					<Table size="small">
						<TableHead>
							<TableRow>
								<TableCell style={{ width: 40 }} />
								<TableCell>Contract #</TableCell>
								<TableCell>Fuel Vendor</TableCell>
								<TableCell>Product</TableCell>
								<TableCell>Start / End Date</TableCell>
								<TableCell>Contracted Gallons</TableCell>
								<TableCell>Purchased Gallons</TableCell>
							</TableRow>
						</TableHead>
						<TableBody>
							{contracts.map((contract) => {
								const { fuelContractId } = contract;
								const isExpanded = expandedContractKeys.has(fuelContractId);
								const vendorBills = contract.vendorBills || [];

								return (
									<React.Fragment key={fuelContractId}>
										<TableRow>
											<TableCell style={{ textAlign: 'center' }}>
												<IconButton
													className={classes.expandToggle}
													size="small"
													onClick={() => this.toggleContractExpanded(fuelContractId)}
													disabled={_.isEmpty(vendorBills)}
												>
													{isExpanded ? <KeyboardArrowDownIcon /> : <KeyboardArrowRightIcon />}
												</IconButton>
											</TableCell>
											<TableCell>
												<a
													href={`/inventoryManagement/fuelContract/update/${fuelContractId}`}
													target="_blank"
													rel="noopener noreferrer"
													style={{ color: '#1976d2', textDecoration: 'none' }}
												>
													{contract.contractNumber || fuelContractId}
												</a>
											</TableCell>
											<TableCell>
												{_.get(
													supplierMap,
													[contract.fuelVendorSupplierId, 'name'],
													contract.fuelVendorSupplierId || '-'
												)}
											</TableCell>
											<TableCell>{contract.productType || '-'}</TableCell>
											<TableCell>
												{fmtDate(contract.startDate)} - {fmtDate(contract.endDate)}
											</TableCell>
											<TableCell>{formatNum(contract.numberOfGallons)}</TableCell>
											<TableCell>{formatNum(contract.totalPurchasedGallons)}</TableCell>
										</TableRow>
										{isExpanded && (
											<TableRow className={classes.deliverySubRow}>
												<TableCell colSpan={7} style={{ padding: '4px 8px 8px 48px' }}>
													{_.isEmpty(vendorBills) ? (
														<div className={classes.noRailcars}>
															No vendor bills linked to this contract
														</div>
													) : (
														<Table size="small" className={classes.deliveryTable}>
															<TableHead>
																<TableRow>
																	<TableCell>Railcar</TableCell>
																	<TableCell>Bill #</TableCell>
																	<TableCell>Bill Date</TableCell>
																	<TableCell>Supplier</TableCell>
																	<TableCell>BOL #</TableCell>
																	<TableCell>Net Gal</TableCell>
																</TableRow>
															</TableHead>
															<TableBody>
																{vendorBills.map((vb) => (
																	<TableRow key={vb.vendorBillOrderId}>
																		<TableCell>
																			<span style={{ fontSize: '0.75rem', color: '#555' }}>
																				{vb.railcarNumber || vb.linkedRailcarId}
																			</span>
																		</TableCell>
																		<TableCell>
																			<a
																				href={`/billing/vendorBill/update/${vb.vendorBillId}`}
																				target="_blank"
																				rel="noopener noreferrer"
																				style={{ color: '#1976d2', textDecoration: 'none' }}
																			>
																				{vb.billNumber || vb.vendorBillId}
																			</a>
																		</TableCell>
																		<TableCell>{vb.billDate ? fmtDate(vb.billDate) : '-'}</TableCell>
																		<TableCell>{vb.supplierName || '-'}</TableCell>
																		<TableCell>{vb.bolNumber || '-'}</TableCell>
																		<TableCell>{formatNum(vb.netGallons)}</TableCell>
																	</TableRow>
																))}
															</TableBody>
														</Table>
													)}
												</TableCell>
											</TableRow>
										)}
									</React.Fragment>
								);
							})}
						</TableBody>
					</Table>
				)}
			</div>
		);
	};

	renderColumnHeader = () => {
		const { classes } = this.props;
		return (
			<div className={classes.columnHeaderRow}>
				{currentColumns.map((col) => (
					<div
						key={col.displayId}
						className={classes.columnHeaderCell}
						style={{ flex: col.flex || 1 }}
					>
						{col.labels ? col.labels.join(' / ') : col.label}
					</div>
				))}
			</div>
		);
	};

	renderGroupRow = (group) => {
		const { classes } = this.props;
		const { terminalMap, supplierMap } = this.state;

		const {
			inventoryPeriodKey,
			terminalId,
			supplierForTerminalId,
			productType,
			fuelVendorSupplierIds,
			unresolvedSupplierRailcarNumbers,
			railcarUnions,
			contracts,
		} = group;
		const terminal = terminalMap[terminalId] || {};

		//- A group is one terminal + supplier account + product, which is the grain fuel
		//- actually settles at. The fuel vendors that supplied it are listed underneath.
		const supplierName = _.get(
			supplierMap,
			[supplierForTerminalId, 'name'],
			supplierForTerminalId ? `Supplier ${supplierForTerminalId}` : 'Unassigned'
		);
		const vendorNames = (fuelVendorSupplierIds || [])
			.map((id) => _.get(supplierMap, [id, 'name'], `Supplier ${id}`))
			.join(', ');

		const totalRailcars = _.sumBy(railcarUnions, (u) => u.railcars.length);
		const totalDeliveries = _.sumBy(railcarUnions, (u) =>
			(u.linkedDeliveryLegs || []).filter((leg) => !leg.isNotInRange).length
		);

		const { gallonsDisplay } = this.renderInventorySummary(group);

		const displayProps = {
			terminalDisplay: (
				<div>
					<div>{terminal.name || terminalId || '-'}</div>
					{!_.isEmpty(unresolvedSupplierRailcarNumbers) && (
						<div style={{ fontSize: 11, color: '#b8860b' }}>
							No contract found for railcar {unresolvedSupplierRailcarNumbers.join(', ')}
						</div>
					)}
				</div>
			),
			supplierDisplay: (
				<div>
					<div>{supplierName}</div>
					{vendorNames && (
						<div style={{ fontSize: 11, opacity: 0.65 }}>via {vendorNames}</div>
					)}
				</div>
			),
			productDisplay: <div>{(productType || '').toUpperCase() || '-'}</div>,
			railcarCountDisplay: <div>{totalRailcars}</div>,
			deliveryCountDisplay: <div>{totalDeliveries}</div>,
			gallonsDisplay,
		};

		return (
			<Accordion key={inventoryPeriodKey} defaultExpanded style={{ marginBottom: 8, overflow: 'hidden' }}>
				<AccordionSummary expandIcon={<ExpandMoreIcon />}>
					<div className={classes.columnRow}>
						{currentColumns.map((col) => (
							<div
								key={col.displayId}
								className={classes.columnCell}
								style={{ flex: col.flex || 1 }}
							>
								<div className={classes.columnLabel}>
									{col.labels ? col.labels.join(' / ') : col.label}
								</div>
								{displayProps[col.displayId]}
							</div>
						))}
					</div>
				</AccordionSummary>
				<AccordionDetails className={classes.detailsSection}>
					{this.renderRailcarTable(railcarUnions)}
					{this.renderContractsTable(contracts)}
				</AccordionDetails>
			</Accordion>
		);
	};

	renderTerminalFilter = () => {
		const { terminalFilterOptions, selectedTerminalIds, terminalMap } = this.state;

		const handleChange = (e) => {
			this.setState({ selectedTerminalIds: e.target.value });
		};

		const renderValue = (selected) => {
			if (selected.length === 0) return 'All terminals';
			return selected
				.map((id) => (terminalMap[id] ? terminalMap[id].name : `Terminal ${id}`))
				.join(', ');
		};

		return (
			<FormControl variant="outlined" size="small" style={{ minWidth: 280, marginBottom: 16 }}>
				<InputLabel>Terminal Filter</InputLabel>
				<Select
					multiple
					value={selectedTerminalIds}
					onChange={handleChange}
					label="Terminal Filter"
					renderValue={renderValue}
				>
					{terminalFilterOptions.map((t) => (
						<MenuItem key={t.terminalId} value={String(t.terminalId)}>
							<Checkbox checked={selectedTerminalIds.includes(String(t.terminalId))} />
							<ListItemText primary={t.name || `Terminal ${t.terminalId}`} />
						</MenuItem>
					))}
				</Select>
			</FormControl>
		);
	};

	renderCalculationNotes = () => {
		const { classes } = this.props;
		const { calculationNotes } = this.state;

		if (_.isEmpty(calculationNotes)) {
			return null;
		}

		return (
			<Accordion style={{ marginBottom: 16 }}>
				<AccordionSummary expandIcon={<ExpandMoreIcon />}>
					<span style={{ fontWeight: 600, fontSize: '0.9rem', color: '#444', textAlign: 'left' }}>
						How These Numbers Are Calculated
					</span>
				</AccordionSummary>
				<AccordionDetails style={{ flexDirection: 'column', padding: '0 24px 16px', alignItems: 'flex-start' }}>
					{calculationNotes.map((note, i) => (
						<div key={i} className={classes.calcNotesItem}>
							{note}
						</div>
					))}
				</AccordionDetails>
			</Accordion>
		);
	};

	renderResults = () => {
		const { classes } = this.props;
		const { terminalSupplierGroups, isSearching, selectedTerminalIds } = this.state;

		if (terminalSupplierGroups === undefined) {
			return (
				<div style={{ color: 'gray', fontStyle: 'italic' }}>
					Click search to see inventory reports
				</div>
			);
		}

		if (isSearching) {
			return (
				<div className={classes.runningSearch}>
					<CircularProgress />
					<span style={{ paddingLeft: 5 }}>Searching...</span>
				</div>
			);
		}

		if (_.isEmpty(terminalSupplierGroups)) {
			return <div style={{ color: 'gray', fontStyle: 'italic' }}>No terminals found</div>;
		}

		const visibleGroups =
			selectedTerminalIds.length === 0
				? terminalSupplierGroups
				: terminalSupplierGroups.filter((g) =>
					selectedTerminalIds.includes(String(g.terminalId))
				);

		return (
			<div>
				{this.renderSummaryBar(visibleGroups)}
				{this.renderColumnHeader()}
				{visibleGroups.map((group) => this.renderGroupRow(group))}
			</div>
		);
	};



	renderAdjustmentDialog = () => {
		const {
			adjustmentDialogGroupKey,
			terminalSupplierGroups,
			terminalMap,
			supplierMap,
			searchedYearMonthEnd,
		} = this.state;

		//- Resolve the group fresh from state each render so a re-search never leaves the
		//- dialog pointed at a stale copy.
		const group = _.find(
			terminalSupplierGroups || [],
			(g) => g.inventoryPeriodKey === adjustmentDialogGroupKey
		);

		if (!group) {
			return null;
		}

		const terminalName = _.get(terminalMap, [group.terminalId, 'name'], `Terminal ${group.terminalId}`);
		const supplierName = _.get(
			supplierMap,
			[group.supplierForTerminalId, 'name'],
			group.supplierForTerminalId ? `Supplier ${group.supplierForTerminalId}` : 'Unassigned'
		);
		const groupTitle = `${terminalName} — ${supplierName} — ${(group.productType || '').toUpperCase()}`;

		return (
			<InventoryAdjustmentDialog
				open={!!adjustmentDialogGroupKey}
				group={group}
				groupTitle={groupTitle}
				periodYearMonth={searchedYearMonthEnd}
				onClose={this.closeAdjustmentDialog}
				onSaved={this.handleAdjustmentSaved}
			/>
		);
	};

	render() {
		const { canRwContracts } = this.props;

		if (!canRwContracts) {
			return <div>Permission denied</div>;
		}

		return (
			<Grid container spacing={2}>
				<Grid item md={12} xs={12}>
					{this.renderSearchOptions()}
				</Grid>
				<Grid item md={12} xs={12}>
					{this.renderCalculationNotes()}
				</Grid>
				<Grid item md={12} xs={12}>
					{this.renderResults()}
				</Grid>

				<ResourcePopup
					open={this.state.isResourcePopupOpen}
					onClose={this.handleCloseResourcePopup}
					resourceIds={this.state.resourceIdsPoppedUp}
					resourceMap={this.state.resourceMap}
				/>

				{this.renderAdjustmentDialog()}

			</Grid>
		);
	}
}

InventoryReports.propTypes = {
	classes: PropTypes.object.isRequired,
	showErrors: PropTypes.func.isRequired,
	canRwContracts: PropTypes.bool,
	history: PropTypes.object,
};

export default _.flow([withStyles(styles, { withTheme: true }), InventoryReportsContainer])(
	InventoryReports
);
